import { useState, useCallback, useMemo } from 'react';
import { parseUKDate, formatDateISO } from '../utils/createConfig.helpers';

export default function useCreateConfigForm({ meterTypeOptions }) {
  const [operator, setOperator] = useState(null);   
  const [unit, setUnit] = useState(null);
  const [evn, setEvn] = useState(null);
  const [meterType, setMeterType] = useState(null); 
  const [meterNumber, setMeterNumber] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [comments, setComments] = useState('');

  const [editingIdx, setEditingIdx] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const isEditing = useMemo(() => editingIdx !== null, [editingIdx]);

  const resetForm = useCallback(() => {
    setOperator(null);
    setUnit(null);
    setEvn(null);
    setMeterType(null);
    setMeterNumber('');
    setFromDate('');
    setComments('');
    setFormErrors({});
    setEditingIdx(null);
  }, []);

  const setOperatorCascade = useCallback((v) => {
    const next = v || null;
    if (next === operator) return;
    setOperator(next);
    if (!isEditing) {
      setUnit(null);
      setEvn(null);
      setMeterNumber('');
      setMeterType(null);
    }
  }, [operator, isEditing]);

  const setUnitCascade = useCallback((v) => {
    const next = v || null;
    if (next === unit) return;
    setUnit(next);
    if (!isEditing) {
      setEvn(null);
      setMeterNumber('');
      setMeterType(null);
    }
  }, [unit, isEditing]);

  const setEvnCascade = useCallback((v) => {
    const next = v || null;
    if (next === evn) return;
    setEvn(next);
    if (!isEditing) {
      setMeterNumber('');
      setMeterType(null);
    }
  }, [evn, isEditing]);

  const beginEdit = useCallback((row, idx) => {
    setEditingIdx(idx);
    setOperator(row.Operator ?? null);
    setUnit(row.Unit ?? null);
    setEvn(row.EVN ?? null);
    setMeterNumber(row.MeterNumber ?? '');
    const meterTypeVal = row.MeterType;
    const matchedMeterType =
      meterTypeOptions.find(o => Number(o.value) === Number(meterTypeVal)) ||
      meterTypeOptions.find(o => o.label?.toLowerCase() === String(meterTypeVal ?? '').toLowerCase()) ||
      null;
    setMeterType(matchedMeterType);
    let formattedDate = '';
    if (row.FromDate) {
      const d = parseUKDate(row.FromDate);
      if (!isNaN(d)) {
        formattedDate = formatDateISO(d);
      }
    }
    setFromDate(formattedDate);
    setComments(row.Comments ?? '');
  }, [meterTypeOptions]);

  return {
    operator, unit, evn, meterType, meterNumber, fromDate, comments,
    editingIdx, formErrors,
    setOperator: setOperatorCascade,
    setUnit: setUnitCascade,
    setEvn: setEvnCascade,
    setMeterType, setMeterNumber, setFromDate, setComments,
    setFormErrors, setEditingIdx,
    resetForm, beginEdit,
  };
}
